import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner } from '@fortawesome/free-solid-svg-icons';
import groupService from '../../api/groupService';
import { showMobileToast } from '../../utils/linkUtils';

const EditDonationProductTypeModal = ({ isOpen, onClose, groupId, productType, onProductTypeUpdated }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  const overlayRef = useRef(null);
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    hasFixedAmount: false,
    amount: '',
    suggestedAmounts: [18, 72, 120, 180],
    allowFeeCoverage: false
  });

  // Form validation
  const [errors, setErrors] = useState({});

  // Initialize form when modal opens or productType changes
  useEffect(() => {
    if (isOpen && productType) {
      // Parse suggested amounts from cents to dollars, or use defaults
      const suggestedAmountsInDollars = productType.suggested_amounts_cents
        ? productType.suggested_amounts_cents.map(cents => cents / 100)
        : [18, 72, 120, 180];

      setFormData({
        name: productType.name || '',
        hasFixedAmount: productType.amount_cents !== null && productType.amount_cents !== undefined,
        amount: productType.amount_cents ? (productType.amount_cents / 100).toFixed(2) : '',
        suggestedAmounts: suggestedAmountsInDollars,
        allowFeeCoverage: productType.allow_fee_coverage || false
      });
      setErrors({});
      setError('');
    }
  }, [isOpen, productType]);

  // Handle form input changes
  const handleInputChange = (field, value, type = 'text') => {
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [field]: value,
        ...(field === 'hasFixedAmount' && !value ? { amount: '' } : {})
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 27) {
      newErrors.name = 'Name must be 27 characters or less';
    }
    
    if (formData.hasFixedAmount) {
      if (!formData.amount || parseFloat(formData.amount) <= 0) {
        newErrors.amount = 'Amount must be greater than $0 when fixed pricing is enabled';
      } else if (parseFloat(formData.amount) > 10000) {
        newErrors.amount = 'Amount cannot exceed $10,000';
      }
    }
    
    // Validate suggested amounts
    for (let i = 0; i < 4; i++) {
      const amount = formData.suggestedAmounts[i];
      if (!amount || parseFloat(amount) <= 0) {
        newErrors[`suggested${i}`] = 'Amount must be greater than $0';
      } else if (parseFloat(amount) > 10000) {
        newErrors[`suggested${i}`] = 'Amount cannot exceed $10,000';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await groupService.updateDonationProductType(groupId, productType.id, {
        name: formData.name.trim(),
        amount: formData.hasFixedAmount ? parseFloat(formData.amount) : null,
        suggested_amounts_cents: formData.suggestedAmounts.map(amt => Math.round(parseFloat(amt) * 100)),
        allow_fee_coverage: formData.allowFeeCoverage
      });
      
      if (response.success) {
        onProductTypeUpdated(response.data);
        showMobileToast('Donation type updated successfully!');
        handleClose();
      } else {
        setError(response.message || 'Failed to update donation type');
      }
    } catch (err) {
      console.error('Error updating donation product type:', err);
      setError(err.response?.data?.message || 'Failed to update donation type');
    } finally {
      setLoading(false);
    }
  };

  // Handle close with animation
  const handleClose = () => {
    if (loading) return;
    
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  // Handle click outside modal
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      handleClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden relative transition-all duration-300 ease-out ${
          isClosing ? 'scale-90 -translate-y-2.5 opacity-0' : 'scale-100 translate-y-0 opacity-100'
        }`}
        ref={modalRef}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-5 flex justify-between items-center bg-white border-b border-gray-100">
          <h2 className="text-xl font-semibold text-slate-800 m-0">Edit Donation Type</h2>
          <button 
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed border-0 bg-transparent cursor-pointer"
            onClick={handleClose}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 bg-white overflow-y-auto scrollbar-hide" style={{ maxHeight: 'calc(90vh - 100px)' }}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-5 text-sm">
              {error}
            </div>
          )}

          {/* Name Field */}
          <div className="flex flex-col gap-2 mb-6">
            <label htmlFor="donation-name" className="block text-sm font-medium text-gray-700">
              Donation Type Name <span className="text-red-500">*</span>
            </label>
            <input
              id="donation-name"
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g., Kiddush Sponsorship, Yahrzeit Memorial"
              className={`w-full px-4 py-3 border rounded-lg text-sm text-gray-900 bg-white transition-all box-border ${
                errors.name 
                  ? 'border-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.1)]' 
                  : 'border-gray-300 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)]'
              } disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed focus:outline-none placeholder:text-gray-400`}
              disabled={loading}
              maxLength={27}
            />
            {errors.name && <span className="block text-red-500 text-xs mt-1.5">{errors.name}</span>}
            <span className="block text-slate-500 text-xs mt-1.5 text-right">{formData.name.length}/27 characters</span>
          </div>

          {/* Fixed Amount Toggle */}
          <div className="flex flex-col gap-2 mb-5">
            <div className="flex items-center mb-2">
              <label className="flex items-center cursor-pointer m-0">
                <input
                  type="checkbox"
                  checked={formData.hasFixedAmount}
                  onChange={(e) => handleInputChange('hasFixedAmount', e.target.checked, 'checkbox')}
                  disabled={loading}
                  className="absolute opacity-0 w-0 h-0 peer"
                />
                <span className="relative inline-block w-11 h-6 bg-slate-300 rounded-full mr-3 flex-shrink-0 transition-all peer-checked:bg-blue-500 before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-5 before:h-5 before:bg-white before:rounded-full before:transition-transform before:shadow-md peer-checked:before:translate-x-5 peer-focus:ring-2 peer-focus:ring-blue-500"></span>
                <span className="text-[15px] font-medium text-gray-800">Fixed Amount</span>
              </label>
            </div>
            <p className="text-[13px] text-gray-600 m-0 leading-snug">
              {formData.hasFixedAmount 
                ? 'Set a specific donation amount' 
                : 'Donors can choose their own amount'}
            </p>
          </div>

          {/* Amount Field */}
          <div className="flex flex-col gap-2 mb-6">
            <label htmlFor="fixed-amount" className="block text-sm font-medium text-gray-700">
              Amount ($) {formData.hasFixedAmount && <span className="text-red-500">*</span>}
            </label>
            <input
              id="fixed-amount"
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              placeholder="0.00"
              min="0"
              step="0.01"
              className={`w-full px-4 py-3 border rounded-lg text-sm text-gray-900 bg-white transition-all box-border ${
                errors.amount 
                  ? 'border-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.1)]' 
                  : 'border-gray-300 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)]'
              } ${
                !formData.hasFixedAmount ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200' : ''
              } focus:outline-none placeholder:text-gray-400`}
              disabled={loading || !formData.hasFixedAmount}
            />
            {errors.amount && <span className="block text-red-500 text-xs mt-1.5">{errors.amount}</span>}
          </div>

          {/* Suggested Amounts for Native App */}
          <div className="flex flex-col gap-2 mb-6 pb-6 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Suggested Amounts (App) <span className="text-red-500">*</span>
            </label>
            <p className="text-[13px] text-gray-600 mb-3 leading-snug">
              Set 4 suggested amounts to display below the donation field. Members can enter any amount or select from these suggestions.
            </p>
            <div className="flex gap-2">
              {[0, 1, 2, 3].map((index) => (
                <div key={index} className="flex-1">
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">$</span>
                    <input
                      id={`suggested-${index}`}
                      type="number"
                      value={formData.suggestedAmounts[index]}
                      onChange={(e) => {
                        const newAmounts = [...formData.suggestedAmounts];
                        newAmounts[index] = e.target.value;
                        handleInputChange('suggestedAmounts', newAmounts);
                      }}
                      placeholder="0"
                      min="0"
                      step="1"
                      className={`w-full pl-5 pr-2 py-2 border rounded-lg text-xs text-gray-900 bg-white transition-all box-border ${
                        errors[`suggested${index}`]
                          ? 'border-red-500 shadow-[0_0_0_3px_rgba(239,68,68,0.1)]'
                          : 'border-gray-300 focus:border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.1)]'
                      } focus:outline-none placeholder:text-gray-400 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed`}
                      disabled={loading}
                    />
                  </div>
                  {errors[`suggested${index}`] && (
                    <span className="block text-red-500 text-[10px] mt-1">{errors[`suggested${index}`]}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Allow Fee Coverage Toggle */}
          <div className="flex flex-col gap-2 mb-6">
            <div className="flex items-center mb-2">
              <label className="flex items-center cursor-pointer m-0">
                <input
                  type="checkbox"
                  checked={formData.allowFeeCoverage}
                  onChange={(e) => handleInputChange('allowFeeCoverage', e.target.checked, 'checkbox')}
                  disabled={loading}
                  className="absolute opacity-0 w-0 h-0 peer"
                />
                <span className="relative inline-block w-11 h-6 bg-slate-300 rounded-full mr-3 flex-shrink-0 transition-all peer-checked:bg-blue-500 before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-5 before:h-5 before:bg-white before:rounded-full before:transition-transform before:shadow-md peer-checked:before:translate-x-5 peer-focus:ring-2 peer-focus:ring-blue-500"></span>
                <span className="text-[15px] font-medium text-gray-800">Allow Fee Coverage</span>
              </label>
            </div>
            <p className="text-[13px] text-gray-600 m-0 leading-snug">
              {formData.allowFeeCoverage
                ? 'Donors can optionally cover your processing fees'
                : 'Donors cannot cover your processing fees - fees will be deducted from the donation'}
            </p>
          </div>

          {/* Save Button */}
          <div className="mt-8 pt-6 flex justify-center">
            <button 
              className="bg-blue-500 border border-blue-500 text-white px-8 py-3 rounded-lg text-[15px] font-medium cursor-pointer transition-all min-w-[180px] flex items-center justify-center gap-2 hover:bg-blue-600 hover:border-blue-600 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(59,130,246,0.3)] disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none sm:w-full"
              onClick={handleSubmit}
              disabled={loading || !formData.name.trim()}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Updating...
                </>
              ) : (
                'Update Donation Type'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default EditDonationProductTypeModal;
